/**
 * Spec 10.3.1 §1 — ledger report print preview. A permanent product surface
 * (lawyers print): renders a statutory report from live data via
 * `render_ledger_report` into an isolated <iframe>, so the report's own print CSS
 * (page breaks, repeated headers, footer totals, statutory identity) governs the
 * output with NO App Shell chrome. "Print" prints the iframe document directly;
 * the toolbar is print-hidden. Sits OUTSIDE AppShellLayout (its own AuthGuard) so
 * nothing from the shell leaks into the page or its print output.
 */
import { useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";

interface RenderResult {
  report: string;
  html: string;
  data: unknown;
}

const REPORT_LABEL: Record<string, string> = {
  trial_balance: "Trial Balance",
  receipts_cashbook: "Receipts Cash Book",
  payments_cashbook: "Payments Cash Book",
  account_ledger: "Account Ledger",
  account_statement: "Statement of Account",
  reconciliation: "Reconciliation Statement",
  transfer_journal: "Transfer Journal",
  overdraw_report: "Overdrawn Report",
  deposit_listing: "Deposit Listing",
  title_audit: "Account Title Audit",
};

export function LedgerPrintPage() {
  const { ledgerId, report } = useParams<{ ledgerId: string; report: string }>();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const params: Record<string, unknown> = {};
  for (const k of ["period", "as_at", "account_id", "reconciliation_id", "deposit_id", "addressee"]) {
    const v = sp.get(k);
    if (v) params[k] = v;
  }
  if (sp.get("statement") === "1") params.statement = true;
  if (sp.get("copy") === "1") params.copy = true;

  const q = useQuery({
    queryKey: ["render_ledger_report", ledgerId, report, sp.toString()],
    enabled: !!ledgerId && !!report,
    queryFn: async () => {
      const r = await mcpClient.toolsCall<RenderResult>("render_ledger_report", { ledger_id: ledgerId, report, params });
      const sc = r.structuredContent as RenderResult | undefined;
      if (!sc?.html) throw new Error((r.content?.[0]?.text as string) ?? "render_failed");
      return sc;
    },
  });

  const print = () => iframeRef.current?.contentWindow?.print();
  // Previews open in a NEW tab (target=_blank), so there is usually no history
  // to pop — navigate(-1) would be a no-op. Fall back to the ledger cockpit.
  const back = () => {
    if (window.history.length > 1) navigate(-1);
    else if (ledgerId) navigate(`/app/trust/${ledgerId}`);
    else navigate("/app");
  };

  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", background: "#525659" }}>
      <div className="print-toolbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px", background: "#1C1B19", color: "#fff", flex: "0 0 auto" }}>
        <button onClick={back} style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,.3)", borderRadius: 6, padding: "5px 12px", cursor: "pointer" }}>← Back</button>
        <strong style={{ fontSize: 14 }}>{REPORT_LABEL[report ?? ""] ?? report}</strong>
        <span style={{ flex: 1 }} />
        <button onClick={print} disabled={!q.data} style={{ background: "#534AB7", color: "#fff", border: "none", borderRadius: 6, padding: "6px 16px", cursor: q.data ? "pointer" : "default", fontWeight: 600, opacity: q.data ? 1 : 0.5 }}>Print / Save as PDF</button>
      </div>
      <div style={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center", padding: 24 }}>
        {q.isLoading ? (
          <div style={{ color: "#ddd", marginTop: 60 }}>Rendering report…</div>
        ) : q.isError ? (
          <div style={{ color: "#fff", marginTop: 60, maxWidth: 460, textAlign: "center" }}>
            <p style={{ fontWeight: 600 }}>Couldn’t render this report</p>
            <p style={{ opacity: 0.8, fontSize: 13 }}>{(q.error as Error)?.message ?? "The report data is unavailable right now."}</p>
            <button onClick={() => q.refetch()} style={{ marginTop: 8, background: "#534AB7", color: "#fff", border: "none", borderRadius: 6, padding: "6px 16px", cursor: "pointer" }}>Retry</button>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            title="report"
            srcDoc={q.data!.html}
            style={{ width: "210mm", minHeight: "297mm", background: "#fff", border: "none", boxShadow: "0 2px 20px rgba(0,0,0,.4)" }}
          />
        )}
      </div>
      <style>{`@media print { .print-toolbar { display: none !important; } }`}</style>
    </div>
  );
}
